import React, { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { LinkProps, useLocation } from 'react-router-dom';

import { env } from '@/config/env';
import { tKeys } from '@/i18n';

import { RoutesEnum } from '@/enums/routes.enum';
import { SearchParamsEnum } from '@/enums/searchParams.enum';
import { TimePeriodEnum } from '@/enums/timePeriod.enum';

import { CircleInfo } from '@/icons/CircleInfo.icon';
import { StadiumIcon } from '@/icons/Stadium.icon';
import { WhistleIcon } from '@/icons/Whistle.icon';

import { useGamezone } from './useGamezone';

interface IHeaderNavigation {
  label: string;
  icon?: React.ReactNode;
  selected: boolean;
  linkData: LinkProps;
}

export const useNavLinks = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { createGamezoneUrl } = useGamezone();

  const createLinkToGZ = useCallback(
    (pathname?: string | undefined) => {
      if (pathname) {
        return createGamezoneUrl({
          pathname,
          searchParams: {
            redirectURL: `${env.GAMEZONE_URL}/${pathname}`,
          },
        });
      }

      return createGamezoneUrl({
        searchParams: {
          redirectURL: env.GAMEZONE_URL,
        },
      });
    },
    [createGamezoneUrl],
  );

  const linkToGZ = createLinkToGZ();

  const desktopNavLinks = useMemo<IHeaderNavigation[]>(
    () => [
      {
        label: t(tKeys.play),
        selected: pathname === RoutesEnum.home,
        linkData: {
          to: RoutesEnum.home,
        },
      },
      {
        label: t(tKeys.leaderboard),
        selected: pathname === RoutesEnum.leaderboard,
        linkData: {
          to: `${RoutesEnum.leaderboard}?${SearchParamsEnum.period}=${TimePeriodEnum.allTime}`,
        },
      },
      {
        label: t(tKeys.prizes),
        selected: pathname === RoutesEnum.prizes,
        linkData: {
          to: RoutesEnum.prizes,
        },
      },
      {
        label: t(tKeys.history),
        selected: pathname === RoutesEnum.history,
        linkData: {
          to: RoutesEnum.history,
        },
      },
    ],
    [pathname, t],
  );

  const navLinks = useMemo<IHeaderNavigation[]>(
    () => desktopNavLinks,
    [desktopNavLinks],
  );

  const sidebarNavLinks = useMemo(
    () => [
      {
        label: t(tKeys.play),
        icon: <WhistleIcon />,
        renderIcon: (props: any) => <WhistleIcon {...props} />,
        selected: pathname === RoutesEnum.home,
        linkData: {
          to: RoutesEnum.home,
        },
      },
      {
        label: t(tKeys.gamezone),
        icon: <StadiumIcon />,
        renderIcon: (props: any) => <StadiumIcon {...props} />,
        selected: false,
        linkData: {
          to: linkToGZ,
        },
      },
      {
        label: t(tKeys.rules),
        icon: <StadiumIcon />,
        renderIcon: (props: any) => <CircleInfo {...props} />,
        selected: pathname === RoutesEnum.rules,
        linkData: {
          to: RoutesEnum.rules,
        },
      },
    ],
    [linkToGZ, t, pathname],
  );

  return { navLinks, sidebarNavLinks, linkToGZ };
};
